import { permissionAllowed, SystemShareType } from '@ews/react-data';
import { useL10n } from '@ews/react-localization-context';
import { dateWithTimezone } from '@ews/zlt-events';
import { IonButton, IonRow, IonText } from '@ionic/react';
import { useCallback } from 'react';
import { useMyDateFormat, useMyPermissions } from '../../../../../../../Login/LoginProvider';
import { useNamedRoutes } from '../../../../../../../NamedRoutes';
import { useDeleteSystemShare, useModifySystemShare, useSystem, useSystemShare, useSystemShareDisplay, useSystemShareNotifications, useSystemShareOperate } from '../../../../../../../ReactData/system';
import { useUserName } from '../../../../../../../ReactData/user';
import SettingsCol from '../../../../../../../Settings/SettingsCol/SettingsCol';
import SettingsCheckbox from '../../../../../../../Settings/SettingsComponents/SettingsCeckbox/SettingsCeckbox';
import SettingsLabel from '../../../../../../../Settings/SettingsComponents/SettingsLabel/SettingsLabel';
import DatePicker from '../../../../../../../Settings/SettingsComponents/SettingsRangeInput/components/DatePicker/DatePicker';
import ValidityCountDown from '../../../ValidityCountDown';
import { Props } from './types';

const General: React.FC<Props> = ({ systemId, userId }) =>
{
    const { timezone } = useSystem(systemId || "");
    const share = useSystemShare(systemId || "", userId || "");
    const shareDisplay = useSystemShareDisplay(systemId || "", userId || "");
    const shareNotifications = useSystemShareNotifications(systemId || "", userId || "");
    const shareOperate = useSystemShareOperate(systemId || "", userId || "");

    const { translate: t } = useL10n();
    const { generate, router } = useNamedRoutes();
    const formatDate = useMyDateFormat();
    const _modifyShare = useModifySystemShare();
    const deleteShare = useDeleteSystemShare();
    const myPermissions = useMyPermissions();

    const modifiedByUser = useUserName(share.modifiedById || "", true);
    const createdByUser = useUserName(share.createdById || "", true);

    const modifiedByUserDisplay = useUserName(shareDisplay.modifiedById || "", true);
    const modifiedByUserNotification = useUserName(shareNotifications.modifiedById || "", true);
    const modifiedByUserOperate = useUserName(shareOperate.modifiedById || "", true);

    const editMode = !permissionAllowed(myPermissions.createUser || 0);

    const modifyShare = useCallback(
        (...args: Parameters<typeof _modifyShare>) =>
        {
            console.log("modifyShare", ...args);
            return _modifyShare(...args);
        },
        [_modifyShare],
    );

    const modifications = [
        {
            t: "display",
            user: modifiedByUserDisplay,
            date: shareDisplay.modified,
            o: shareDisplay,
        },
        {
            t: "notifications",
            user: modifiedByUserNotification,
            date: shareNotifications.modified,
            o: shareNotifications,

        },
        {
            t: "operate",
            user: modifiedByUserOperate,
            date: shareOperate.modified,
            o: shareOperate,

        },
        {
            t: "share",
            user: modifiedByUser,
            date: share.modified,
            o: share,
        },
    ];
    modifications.sort((a, b) =>
    {
        if (!a.date) {
            if (!b.date) return 0;
            return 1;
        }
        if (!b.date) return -1;

        if (a.date < b.date) return 1;
        else if (a.date > b.date) return -1;
        return 0;
    });
    const lastModifiedDate = modifications[0].date;
    const lastModifiedByUser = modifications[0].user;


    const handleDelete = async (withRedirect: boolean = false) =>
    {
        await deleteShare(share.systemId!, share.userId!);

        if (withRedirect) {
            const redirect = generate('system:shares', { id: systemId });
            router?.push(redirect);
        }
    };


    return (<>
        <SettingsCol>
            <SettingsLabel text={'COM_FACPMANAGEMENT_LOGS_MAIL_ANLAGENID'} value={share.systemNumber?.toString()} />
            <SettingsLabel
                text="COM_FACPMANAGEMENT_CUSTOMER_FIELD_CREATED_BY_USER_LABEL"
                value={createdByUser} />
            <SettingsLabel text={'COM_FACPMANAGEMENT_AUTOMATEDMAIL_CREATED'} value={share.created ? formatDate(new Date(share.created)) : ""} />
            <SettingsLabel
                text="COM_FACPMANAGEMENT_CUSTOMER_FIELD_MODIFIED_BY_USER_LABEL"
                value={lastModifiedByUser} />
            <SettingsLabel text={'[REACT_MODIFIED_AT]'} value={lastModifiedDate ? formatDate(new Date(lastModifiedDate)) : ""} />
            <SettingsLabel text={'COM_FACPMANAGEMENT_USERS_USERID'} value={share.username} />
            <SettingsLabel text={'TD_USERNAME'} value={[share.userLastName, share.userFirstName].join(", ")} />
            <SettingsLabel text={'[REACT_SHARE_TYPE]'} value={t(`[REACT_SHARE_TYPE_${share.type}]`)} />
            {share.type === SystemShareType.MODIFIED &&
                <IonRow class="ion-justify-content-end">
                    <IonButton
                        disabled={editMode}
                        color={"success"}
                        onClick={() => handleDelete()}
                    >
                        {t('COM_FACPMANAGEMENT_SHARE_DELETE_DETAILS_STANDARD')}
                    </IonButton>
                </IonRow>
            }

            {share.type === SystemShareType.EXTENDED &&
                <>

                    <SettingsCheckbox
                        disabled={editMode}
                        text={"[REACT_ENABLEMENT_RELEASE_TIME]"}
                        checked={share.validFor !== -1}
                        onChange={(validFor) =>
                        {
                            if (validFor) {


                                const from = dateWithTimezone(timezone);
                                const to = new Date(from.getTime() + (2 * 3600) * 1000);

                                modifyShare(share.systemId!, share.userId!, { validFor: 1, validFrom: from, validTo: to });
                            } else {
                                modifyShare(share.systemId!, share.userId!, { validFor: -1, validFrom: null, validTo: null });
                            }
                        }}>

                        {/*                         <IonRadioGroup
                            value={durationOrInterval}
                            onIonChange={radioButtonHandlerDurationOrInterval}
                        >
                            <IonRadio
                                labelPlacement={"end"}
                                alignment="center"
                                value="duration">
                                {t("[REACT_CREATE_SYSTEM_SHARE_DURATION]")}
                            </IonRadio>
                            <IonRadio
                                labelPlacement={"end"}
                                alignment="center"
                                value="interval">
                                {t("[REACT_CREATE_SYSTEM_SHARE_INTERVALL]")}
                            </IonRadio>
                        </IonRadioGroup> */}

                        {/* <SettingsTextInput
                            disabled={!editMode && durationOrInterval !== "duration"}
                            lines="none"
                            text={"[REACT_FOR_HOW_MANY_HOURS]"}
                            textAlign='right'
                            value={share.validFor}
                            onBlur={({ value }) =>
                            {
                                if (value !== share.validFor) {
                                    modifyShare(share.systemId!, share.userId!, { validFor: value, validFrom: new Date() });
                                }
                            }}>

                        </SettingsTextInput> */}

                        {!editMode /* && durationOrInterval === "interval" */ ?
                            <DatePicker
                                childText={{ start: "[REACT_START]", end: "COM_FACPMANAGEMENT_END_EVENT" }}
                                value={{ from: (share.validFrom as unknown as string), to: (share.validTo as unknown as string) }}
                                divider={false}
                                position="row"
                                presentation='date-time'

                                onChange={(range) =>
                                {
                                    try {
                                        const from = new Date(range.from!);
                                        const to = new Date(range.to!);

                                        //console.log(range.from, from, range.to, to);
                                        /*
                                        let hoursBetween = wholeHoursBetween(newDateFrom, newDateTo);
                                        if (hoursBetween <= 0) {
                                            hoursBetween = 1;
                                        }

                                        if (!dateEqual(newDateFrom, new Date(share.validFrom ?? NaN)!) || hoursBetween !== share.validFor) {
                                            // console.log("date really changed", newDateFrom, share.validFrom, hoursBetween, share.validFor);
                                            modifyShare(share.systemId!, share.userId!, { validFor: hoursBetween, validFrom: newDateFrom });
                                        } */

                                        modifyShare(share.systemId!, share.userId!, { validFor: 1, validFrom: from, validTo: to });
                                    }
                                    catch (e) {
                                        console.error("Exception in General.tsx in DatePicker.onChange", e);
                                    }
                                }}
                            />
                            : null
                        }
                        <SettingsLabel disabled={true} text={'COM_ADMIN_USER_FIELD_TIMEZONE_LABEL'} value={timezone} />
                    </SettingsCheckbox>

                    <SettingsLabel text={'[REACT_VALID_FOR]'} children={share.validFor! >= 0 ? <ValidityCountDown validFrom={share.validFromUTC!} validTo={share.validToUTC!} /> : <IonText style={{ float: "right" }}>{t('COM_BANNERS_FIELD_VALUE_1')}</IonText>} />
                    <IonRow class="ion-justify-content-end">
                        <IonButton
                            disabled={editMode}
                            color={"danger"}
                            onClick={() => handleDelete(true)}
                        >
                            {t('COM_FACPMANAGEMENT_SHARE_DELETE_DETAILS')}
                        </IonButton>
                    </IonRow>
                </>
            }
        </SettingsCol >
    </>);
};
export default General;
